import { jobAccent, jobIcon, onAccent, type JobLook } from '@/lib/jobStyle';
import { cn } from '@/lib/utils';

const SIZES = {
  xs: { box: 'size-5', icon: 'size-3' },
  sm: { box: 'size-7', icon: 'size-4' },
  md: { box: 'size-9', icon: 'size-5' },
  lg: { box: 'size-12', icon: 'size-6' },
  xl: { box: 'size-16', icon: 'size-8' },
} as const;

/**
 * A job's mark: its cover photo cropped square when it has one, otherwise its icon on its accent
 * colour. Jobs without a chosen colour get a stable one derived from their name, so the mark is
 * always coloured and always the same for the same job. Decorative unless a `title` is passed.
 */
export function JobMark({ job, size = 'sm', className, title }: { job: JobLook; size?: keyof typeof SIZES; className?: string; title?: string }) {
  const s = SIZES[size];
  const accent = jobAccent(job);
  const Icon = jobIcon(job.icon);
  if (job.coverUrl) {
    return <img src={job.coverUrl} alt={title ?? ''} title={title} className={cn(s.box, 'shrink-0 object-cover', className)} style={{ boxShadow: `inset 0 0 0 1px ${accent}` }} />;
  }
  return (
    <span className={cn(s.box, 'grid shrink-0 place-items-center', className)} style={{ backgroundColor: accent, color: onAccent(accent) }} title={title} aria-hidden={title ? undefined : true}>
      <Icon className={s.icon} strokeWidth={1.75} />
    </span>
  );
}

/** Thin accent line, e.g. along the top of a card or header. */
export function JobAccentBar({ job, className }: { job: JobLook; className?: string }) {
  return <span className={cn('block h-0.5 w-full', className)} style={{ backgroundColor: jobAccent(job) }} aria-hidden />;
}
